import { useState } from "react";
import { api, apiUrl, type Me } from "../lib/api";

/**
 * Account connections: link Twitch and/or YouTube to one account, log in with
 * either, and remove a provider. A login is one provider identity; an account
 * spans 1–2 of them (see api/src/lib/accounts.ts).
 *
 * Removing a provider while another remains is an unlink. Removing the *only*
 * provider is account deletion, gated behind a type-the-name confirm modal.
 * Linking / reconnecting goes through OAuth (`?link=1`) purely for identity —
 * this panel never requests YouTube's elevated (chat) scope, so a missing
 * `hasChatScope` here is the normal/expected state, not something to flag.
 * Chat scope is only ever requested from Settings' Chat connection panel
 * (SettingsPanel.tsx's ChatConnection, `?scope=chat`), which is the one flow
 * whose purpose is actually enabling chat.
 */
const PLATFORMS = [
  { id: "twitch", label: "Twitch" },
  { id: "youtube", label: "YouTube" },
] as const;

export function ConnectionsPanel({ me, onChanged }: { me: Me; onChanged: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const linkedCount = me.connections.filter((c) => c.connected).length;

  function authorize(platform: string) {
    // A fresh authorize guarantees the token carries the chat scope; the
    // callback links the identity and returns here.
    const returnTo = encodeURIComponent("/account");
    window.location.href = apiUrl(`/auth/${platform}?link=1&returnTo=${returnTo}`);
  }

  async function removeProvider(platform: "twitch" | "youtube") {
    // The last provider can't be unlinked — that's account deletion.
    if (linkedCount <= 1) {
      setConfirmDelete(true);
      return;
    }
    if (!window.confirm(`Remove ${platform} from this account?`)) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api.unlinkProvider(platform);
      if (res.loggedOut) {
        window.location.href = "/login"; // removed the provider we were logged in with
        return;
      }
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="panel">
      <h2>Connections</h2>
      <p className="muted" style={{ marginTop: 0 }}>
        Signed in as <strong>{me.displayName}</strong>. Link both platforms to log
        in with either.
      </p>

      <ul className="connections">
        {PLATFORMS.map(({ id, label }) => {
          const conn = me.connections.find((c) => c.platform === id);
          const connected = Boolean(conn?.connected);
          const isCurrent = me.platform === id;
          const needsReauth = connected && conn!.expired;

          return (
            <li key={id} className="connection-row">
              <span className={`badge platform ${id}`}>{label}</span>

              <div className="connection-detail">
                <span className={`live ${connected ? "on" : ""}`}>
                  <span className="dot" />
                  {connected ? "Connected" : "Not connected"}
                  {connected && isCurrent && " · current session"}
                </span>
                {needsReauth && (
                  <span className="connection-warn">
                    Session expired — reconnect to keep chat working.
                  </span>
                )}
              </div>

              {connected ? (
                <div className="connection-actions">
                  {needsReauth && (
                    <button className="btn" disabled={busy} onClick={() => authorize(id)}>
                      Reconnect
                    </button>
                  )}
                  <button
                    className="btn danger"
                    disabled={busy}
                    onClick={() => removeProvider(id)}
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <button className="btn primary" disabled={busy} onClick={() => authorize(id)}>
                  Connect
                </button>
              )}
            </li>
          );
        })}
      </ul>

      {error && <div className="error" style={{ marginTop: 12 }}>{error}</div>}

      {confirmDelete && (
        <DeleteAccountModal
          displayName={me.displayName}
          onCancel={() => setConfirmDelete(false)}
        />
      )}
    </div>
  );
}

/**
 * Complex confirm for deleting the account (the only path to removing the last
 * provider). Requires typing the account's display name exactly, so it can't be
 * triggered by a stray click. On success the server clears the session; we send
 * the user to /login.
 */
function DeleteAccountModal({
  displayName,
  onCancel,
}: {
  displayName: string;
  onCancel: () => void;
}) {
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const armed = typed.trim() === displayName;

  async function doDelete() {
    setBusy(true);
    setError(null);
    try {
      await api.deleteAccount();
      window.location.href = "/login";
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" onClick={onCancel}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>Delete account</h2>
        <p className="muted" style={{ marginTop: 0 }}>
          This removes your last connected platform, which deletes the whole
          account and its stored logins. This <strong>cannot be undone</strong>.
        </p>
        <p className="muted">
          Type <strong>{displayName}</strong> to confirm.
        </p>
        <input
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          placeholder={displayName}
          autoFocus
          spellCheck={false}
        />
        {error && <div className="error" style={{ marginTop: 12 }}>{error}</div>}
        <div className="connection-actions" style={{ marginTop: 16, justifyContent: "flex-end" }}>
          <button className="btn ghost" disabled={busy} onClick={onCancel}>
            Cancel
          </button>
          <button className="btn danger-solid" disabled={!armed || busy} onClick={doDelete}>
            {busy ? "Deleting…" : "Delete account"}
          </button>
        </div>
      </div>
    </div>
  );
}
